import React from 'react';
import { Link } from 'react-router-dom';
import ScrollFadeIn from '../components/ScrollFadeIn';
import styled, { keyframes } from 'styled-components';
import {
  Mic,
  UsersRound,
  GraduationCap,
  ArrowRight,
  Boxes,
  FileCheck,
  Brain,
  Scale,
  BadgeCheck
} from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <HeroEyebrow>FREAKIT · AI ASSESSMENT COMPANY</HeroEyebrow>
          <HeroTitle>
            <SocraAccent>교육</SocraAccent>을 더 깊게,<br />
            <HuraAccent>사람</HuraAccent>을 더 정확하게.
          </HeroTitle>
          <HeroSubtitle>
            FreakIT는 AI가 질문하고 근거를 남기는 평가 기술로<br />
            배움과 사람에 대한 더 나은 판단을 돕습니다.
          </HeroSubtitle>
          <HeroButtons>
            <PrimaryButton to="/socra">SOCRATEST 알아보기</PrimaryButton>
            <HuraButton to="/hura">HuRA 알아보기</HuraButton>
            <AchievementLink to="/achievements">
              FreakIT의 성과 보기 <ArrowRight />
            </AchievementLink>
          </HeroButtons>
          <HeroSolutions aria-label="FreakIT 핵심 솔루션">
            <HeroSolution to="/socra" data-tone="socra">
              <HeroSolutionName>SOCRATEST</HeroSolutionName>
              <HeroSolutionText>교재 근거를 따라 깊이 묻는 AI 교육 평가</HeroSolutionText>
              <ArrowRight />
            </HeroSolution>
            <HeroSolution to="/hura" data-tone="hura">
              <HeroSolutionName>HuRA</HeroSolutionName>
              <HeroSolutionText>행동 근거를 구조화하는 AI HR 역량 평가</HeroSolutionText>
              <ArrowRight />
            </HeroSolution>
          </HeroSolutions>
        </HeroContent>
      </HeroSection>

      <ProductsSection>
        <SectionTitle>우리의 대표 솔루션</SectionTitle>
        <SectionSubtitle>
          IT 기술과 교육의 완벽한 결합
        </SectionSubtitle>

        <ProductsGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <SocraProductCard to="/socra">
              <ProductIcon><Mic /></ProductIcon>
              <ProductCardContent>
                <FlagshipBadge>FLAGSHIP</FlagshipBadge>
                <ProductName>SOCRATEST</ProductName>
                <ProductTagline>AI 기반 실시간 구술·텍스트 시험 플랫폼</ProductTagline>
                <ProductDescription>
                  교수자가 평가목표와 질문 흐름을 설계하면 AI가 교재 근거 안에서
                  학생 답변에 맞는 소크라테스식 후속 질문을 이어갑니다. 응시 후에는
                  평가목표별 내용 점수와 구술 전달력 참고 지표, 답변 근거와 불확실성을 담은
                  채점 초안을 제공하며 최종 점수는 담당 교원이 검토·확정합니다.
                  화면·웹캠 원본과 객관적 브라우저 사건은 자동 감점 없이 무결성 검토에만 활용됩니다.
                </ProductDescription>
                <ProductFeatures>
                  <Feature>✓ 교수 확정 평가목표·루브릭 기반 출제 설계</Feature>
                  <Feature>✓ 교재 범위 안의 실시간 소크라테스식 후속 질문</Feature>
                  <Feature>✓ 구술·텍스트 시험과 STT 답변 확인</Feature>
                  <Feature>✓ 근거·불확실성을 함께 제시하는 AI 채점 초안</Feature>
                  <Feature>✓ 화면·웹캠 원본과 객관적 사건 기반 무결성 검토</Feature>
                </ProductFeatures>
              </ProductCardContent>
            </SocraProductCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.2}>
            <HuraProductCard to="/hura">
              <HuraProductIcon><UsersRound /></HuraProductIcon>
              <ProductCardContent>
                <HuraFlagshipBadge>CORE PRODUCT</HuraFlagshipBadge>
                <ProductName>HuRA</ProductName>
                <HuraProductTagline>AI 기반 구조화 HR 역량 평가 솔루션</HuraProductTagline>
                <ProductDescription>
                  SOCRATEST의 소크라틱 트리 엔진을 HR 평가에 확장했습니다.
                  직무별 역량과 루브릭을 설계하고, BEI 기반 구조화 인터뷰로 후보자의
                  실제 행동 근거를 수집합니다. 인터뷰 종료 후에는 역량별 점수와 핵심 발화,
                  강점·우려 사항을 담은 설명 가능한 HR 리포트를 제공해 사람의 최종 판단을 지원합니다.
                </ProductDescription>
                <ProductFeatures>
                  <Feature>✓ 직무별 역량 모델링과 평가 템플릿</Feature>
                  <Feature>✓ BEI 기반 AI 구조화 인터뷰와 실시간 후속 질문</Feature>
                  <Feature>✓ 핵심 발화에 연결된 루브릭 기반 채점</Feature>
                  <Feature>✓ 강점·우려 사항·HR 가이드를 담은 즉시 리포트</Feature>
                  <Feature>✓ 사람의 검토·확정과 감사 가능한 평가 기록</Feature>
                </ProductFeatures>
              </ProductCardContent>
            </HuraProductCard>
          </ScrollFadeIn>
        </ProductsGrid>
      </ProductsSection>


      <TechProjectSection>
        <SectionTitle>우리의 사이드 프로젝트</SectionTitle>
        <SectionSubtitle>
          FreakIT가 개발한 혁신적인 사이드 프로젝트들을 만나보세요.
        </SectionSubtitle>
        
        <OSGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <OSCard to="/projects/aristo">
              <OSIconWrapper><Mic /></OSIconWrapper>
              <OSCardBody>
                <OSTitle>ARISTO</OSTitle>
                <OSTagline>AI 음성 튜터</OSTagline>
                <OSDescription>
                  업로드한 문서를 AI가 학습하고 실시간 소크라테스식 문답을 진행.
                </OSDescription>
                <OSLink>자세히 보기 <ArrowRight /></OSLink>
              </OSCardBody>
            </OSCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.2}>
            <OSCard to="/kaistudy">
              <OSIconWrapper><GraduationCap /></OSIconWrapper>
              <OSCardBody>
                <OSTitle>KAIStudy</OSTitle>
                <OSTagline>KAIST 학생 그룹 과외</OSTagline>
                <OSDescription>
                  KAIST 재학생이 직접 진행하는 소규모 맞춤형 프리미엄 그룹 과외.
                </OSDescription>
                <OSLink>자세히 보기 <ArrowRight /></OSLink>
              </OSCardBody>
            </OSCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.3}>
            <OSCard to="/projects/vta">
              <OSIconWrapper><Boxes /></OSIconWrapper>
              <OSCardBody>
                <OSTitle>VTA</OSTitle>
                <OSTagline>가치관 유형 검사</OSTagline>
                <OSDescription>
                  응답 패턴을 분석하여 개인의 핵심 가치관을 진단하는 심리 테스트.
                </OSDescription>
                <OSLink>자세히 보기 <ArrowRight /></OSLink>
              </OSCardBody>
            </OSCard>
          </ScrollFadeIn>
        </OSGrid>
      </TechProjectSection>

      <ValueSection>
        <ValueContent>
          <ValueTextSection>
            <ValueTitle>우리의 가치</ValueTitle>
            <ValueDescription>
              FreakIT는 기술과 교육의 경계를 허물고,
              학습자 중심의 혁신적인 솔루션을 만들어갑니다.
            </ValueDescription>
          </ValueTextSection>

          <ValuesGrid>
            <ScrollFadeIn direction="up" delay={0.1}>
              <ValueCard>
                <ValueIcon><FileCheck /></ValueIcon>
                <ValueCardTitle>근거 우선</ValueCardTitle>
                <ValueCardDescription>
                  출처 없는 답은 내지 않습니다. 모든 AI 출력은 교재에서 검증됩니다
                </ValueCardDescription>
              </ValueCard>
            </ScrollFadeIn>

            <ScrollFadeIn direction="up" delay={0.2}>
              <ValueCard>
                <ValueIcon><Brain /></ValueIcon>
                <ValueCardTitle>깊이 있는 평가</ValueCardTitle>
                <ValueCardDescription>
                  쉬운 것이 아니라 중요한 것을 측정합니다. 정답 너머의 사고를 봅니다
                </ValueCardDescription>
              </ValueCard>
            </ScrollFadeIn>

            <ScrollFadeIn direction="up" delay={0.3}>
              <ValueCard>
                <ValueIcon><Scale /></ValueIcon>
                <ValueCardTitle>공정성 설계</ValueCardTitle>
                <ValueCardDescription>
                  같은 기준을 누구에게나. 투명한 기록으로 평가의 신뢰를 지킵니다
                </ValueCardDescription>
              </ValueCard>
            </ScrollFadeIn>

            <ScrollFadeIn direction="up" delay={0.4}>
              <ValueCard>
                <ValueIcon><BadgeCheck /></ValueIcon>
                <ValueCardTitle>현장에서 증명</ValueCardTitle>
                <ValueCardDescription>
                  슬라이드가 아닌 강의실에서 검증합니다. 실제 도입으로 입증합니다
                </ValueCardDescription>
              </ValueCard>
            </ScrollFadeIn>
          </ValuesGrid>
        </ValueContent>
      </ValueSection>

      <CTASection>
        <CTAContent>
          <CTATitle>FreakIT와 함께 시작하세요</CTATitle>
          <CTADescription>
            혁신적인
            교육 솔루션으로 새로운 가능성을 경험하세요
          </CTADescription>
          <CTAButton to="/contact">지금 문의하기</CTAButton>
        </CTAContent>
      </CTASection>
    </HomeContainer>
  );
};

export default HomePage;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const HomeContainer = styled.div`
  min-height: 100vh;
`;
const HeroSection = styled.section`
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8.5rem 2rem 4.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: auto;
    padding: 7.5rem 1.5rem 3.5rem;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1120px;
  color: white;
  animation: ${fadeIn} 0.8s ease-out;
  z-index: 1;
`;

const HeroEyebrow = styled.p`
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  line-height: 1.6;
`;

const HeroTitle = styled.h1`
  max-width: 1040px;
  font-size: clamp(3.4rem, 7vw, 6.2rem);
  font-weight: 850;
  line-height: 1.08;
  letter-spacing: -0.06em;
  margin-bottom: 2rem;
  word-break: keep-all;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: clamp(2.8rem, 10vw, 4.4rem);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: clamp(2.5rem, 12vw, 3.3rem);
    line-height: 1.13;
  }
`;

const SocraAccent = styled.span`
  color: #7fb0d4;
`;

const HuraAccent = styled.span`
  color: #62d7c5;
`;

const HeroSubtitle = styled.p`
  max-width: 760px;
  font-size: 1.2rem;
  line-height: 1.85;
  margin-bottom: 2.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: keep-all;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    br {
      display: none;
    }
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 0.9rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  padding: 0.9rem 1.5rem;
  background: #ffffff;
  color: #020617;
  border-radius: 7px;
  font-weight: 700;
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  border: 1px solid #ffffff;

  &:hover {
    transform: translateY(-2px);
    background: #f8fafc;
  }
`;

const HuraButton = styled(Link)`
  padding: 0.9rem 1.5rem;
  background: rgba(94, 234, 212, 0.07);
  color: #8ee7da;
  border-radius: 7px;
  font-weight: 700;
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  border: 1px solid rgba(98, 215, 197, 0.55);

  &:hover {
    background: rgba(94, 234, 212, 0.13);
    transform: translateY(-2px);
  }
`;

const AchievementLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.9rem 0.4rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.98rem;
  font-weight: 650;

  svg {
    width: 1rem;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const HeroSolutions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 4.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    margin-top: 3.5rem;
  }
`;

const HeroSolutionName = styled.strong`
  font-size: 1.25rem;
  letter-spacing: -0.02em;
`;

const HeroSolutionText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.93rem;
  line-height: 1.65;
  word-break: keep-all;
`;

const HeroSolution = styled(Link)`
  display: grid;
  grid-template-columns: minmax(120px, 0.65fr) 1fr auto;
  align-items: center;
  gap: 1.25rem;
  min-height: 112px;
  padding: 1.3rem 1.5rem 1.3rem 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.cubic};

  & + & {
    padding-left: 1.5rem;
    border-left: 1px solid rgba(148, 163, 184, 0.25);
  }

  &[data-tone='socra'] ${HeroSolutionName} {
    color: #8ab8d9;
  }

  &[data-tone='hura'] ${HeroSolutionName} {
    color: #62d7c5;
  }

  svg {
    width: 1.1rem;
    color: ${({ theme }) => theme.colors.textTertiary};
    transition: transform 0.2s ease;
  }

  &:hover {
    color: #ffffff;
  }

  &:hover svg {
    transform: translateX(4px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: minmax(110px, 0.5fr) 1fr auto;
    padding-right: 0;

    & + & {
      padding-left: 0;
      border-left: 0;
      border-top: 1px solid rgba(148, 163, 184, 0.25);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr auto;
    gap: 0.5rem 1rem;

    ${HeroSolutionText} {
      grid-column: 1 / -1;
    }

    svg {
      grid-column: 2;
      grid-row: 1;
    }
  }
`;
const ProductsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;
const SectionTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2.5rem;
`;
const ProductsGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;

  > * {
    height: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
const ProductCard = styled(Link)`
  height: 100%;
  background: rgba(15, 23, 42, 0.42);
  border-radius: 10px;
  padding: 2rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 2px solid ${({ theme }) => theme.colors.accent};
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  align-items: flex-start;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 255, 255, 0.22);
    background: rgba(15, 23, 42, 0.64);
  }
`;

const SocraProductCard = styled(ProductCard)``;

const HuraProductCard = styled(ProductCard)`
  border-top-color: #62d7c5;
`;

const ProductIcon = styled.div`
  font-size: 2.4rem;
  margin-top: 0.25rem;
  color: ${({ theme }) => theme.colors.accent};
`;
const HuraProductIcon = styled(ProductIcon)`
  color: #62d7c5;
`;

const ProductCardContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const FlagshipBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  margin-bottom: 0.9rem;
  background: rgba(127, 176, 212, 0.12);
  border: 1px solid rgba(127, 176, 212, 0.4);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: ${({ theme }) => theme.colors.accent};
`;
const HuraFlagshipBadge = styled(FlagshipBadge)`
  color: #62d7c5;
  background: rgba(94, 234, 212, 0.08);
  border-color: rgba(94, 234, 212, 0.35);
`;

const ProductName = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const ProductTagline = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
  margin-bottom: 1.5rem;
`;
const HuraProductTagline = styled(ProductTagline)`
  color: #62d7c5;
`;

const ProductDescription = styled.p`
  font-size: 1rem;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  word-break: keep-all;
`;

const ProductFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Feature = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textTertiary};
  font-weight: 500;
`;

/* ── Tech Project Section ── */
const TechProjectSection = styled.section`
  padding: 5rem 2rem;
  background: transparent;
`;
const OSGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;
const OSCard = styled(Link)`
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(15, 23, 42, 0.4);
  }
`;
const OSIconWrapper = styled.div`
  font-size: 1.9rem;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.5rem;
`;
const OSCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const OSTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const OSTagline = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryDark};
  margin-bottom: 1rem;
`;
const OSDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 2rem;
  flex: 1;
`;
const OSLink = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: gap 0.2s ease;
  ${OSCard}:hover & {
    gap: 0.75rem;
  }
`;

const ValueSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const ValueContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;
const ValueTextSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ValueTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;

const ValueDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 800px;
  margin: 0 auto;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.75rem;
`;
const ValueCard = styled.div`
  padding: 1.75rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(8px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(15, 23, 42, 0.4);
  }
`;
const ValueIcon = styled.div`
  font-size: 1.9rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const ValueCardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const ValueCardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const CTADescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.15rem;
  }
`;
const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  border: none;
  font-size: 1.2rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
`;
